import React, { useState } from "react";

import {
  Bell,
  Check,
  ChevronDown,
  LogOut,
  LucideIcon,
  Monitor,
  Moon,
  Search,
  Sun,
  User,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

interface ThemeOption {
  value: "light" | "dark" | "system";
  label: string;
  icon: LucideIcon;
}

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { globalConfig, notifications, unreadCount } = useApp();
  const { logout } = useAuth();
  const [showNotifications, setShowNotifications] = useState<boolean>(false);

  const themeOptions: ThemeOption[] = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ];

  const envColors: Record<string, string> = {
    PROD: "bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800",
    STAGING:
      "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800",
    DEV: "bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800",
  };

  return (
    <header
      data-testid="header"
      className={cn(
        "fixed top-0 right-0 h-16 z-20",
        "bg-white/80 dark:bg-slate-900/80 backdrop-blur-md",
        "border-b border-slate-200 dark:border-slate-800",
        "flex items-center justify-between px-6",
        "transition-all duration-200",
        collapsed ? "left-16" : "left-64",
      )}
    >
      {/* Left Side - App Name & Environment */}
      <div className="flex items-center gap-4">
        <h1
          data-testid="header-app-name"
          className="font-heading font-bold text-lg text-slate-900 dark:text-slate-100 hidden md:block"
        >
          SmartStore Admin
        </h1>
        <span
          data-testid="header-env-badge"
          className={cn(
            "px-2.5 py-1 text-xs font-bold rounded-md border",
            envColors[globalConfig.environment] || envColors.PROD,
          )}
        >
          {globalConfig.environment}
        </span>
      </div>

      {/* Center - Global Search */}
      <div className="flex-1 max-w-xl mx-8 hidden sm:block">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="global-search-input"
            type="text"
            placeholder="Search sellers, orders, SKUs, workspaces…"
            className={cn(
              "w-full h-10 pl-10 pr-4 rounded-lg",
              "bg-slate-100 dark:bg-slate-800",
              "border border-transparent",
              "text-sm text-slate-900 dark:text-slate-100",
              "placeholder:text-slate-400 dark:placeholder:text-slate-500",
              "focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20",
              "transition-colors duration-150",
            )}
          />
        </div>
      </div>

      {/* Right Side - Actions */}
      <div className="flex items-center gap-2">
        {/* Theme Toggle */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              data-testid="theme-toggle-btn"
              className={cn(
                "p-2 rounded-md",
                "text-slate-500 dark:text-slate-400",
                "hover:bg-slate-100 dark:hover:bg-slate-800",
                "transition-colors duration-150",
              )}
            >
              {resolvedTheme === "dark" ? (
                <Moon className="w-5 h-5" strokeWidth={1.5} />
              ) : (
                <Sun className="w-5 h-5" strokeWidth={1.5} />
              )}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              data-testid="theme-dropdown"
              className={cn(
                "min-w-[140px] py-1 rounded-md z-50",
                "bg-white dark:bg-slate-900",
                "border border-slate-200 dark:border-slate-800",
                "shadow-lg animate-fade-in",
              )}
              align="end"
              sideOffset={4}
            >
              {themeOptions.map((option) => (
                <DropdownMenu.Item
                  key={option.value}
                  data-testid={`theme-option-${option.value}`}
                  onClick={() => setTheme(option.value)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                    "hover:bg-slate-50 dark:hover:bg-slate-800",
                    "outline-none",
                    theme === option.value
                      ? "text-indigo-600 dark:text-indigo-400"
                      : "text-slate-700 dark:text-slate-300",
                  )}
                >
                  <option.icon className="w-4 h-4" />
                  <span className="flex-1">{option.label}</span>
                  {theme === option.value && <Check className="w-4 h-4" />}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {/* Notifications */}
        <DropdownMenu.Root
          open={showNotifications}
          onOpenChange={setShowNotifications}
        >
          <DropdownMenu.Trigger asChild>
            <button
              data-testid="notifications-btn"
              className={cn(
                "relative p-2 rounded-md",
                "text-slate-500 dark:text-slate-400",
                "hover:bg-slate-100 dark:hover:bg-slate-800",
                "transition-colors duration-150",
              )}
            >
              <Bell className="w-5 h-5" strokeWidth={1.5} />
              {unreadCount > 0 && (
                <span
                  data-testid="notifications-badge"
                  className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold"
                >
                  {unreadCount}
                </span>
              )}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              data-testid="notifications-dropdown"
              className={cn(
                "w-80 py-2 rounded-md z-50",
                "bg-white dark:bg-slate-900",
                "border border-slate-200 dark:border-slate-800",
                "shadow-lg animate-fade-in",
              )}
              align="end"
              sideOffset={4}
            >
              <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">
                <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                  Notifications
                </h3>
              </div>
              {notifications.length > 0 ? (
                notifications.map((notif) => (
                  <DropdownMenu.Item
                    key={notif.id}
                    data-testid={`notification-item-${notif.id}`}
                    className={cn(
                      "px-3 py-2.5 cursor-pointer outline-none",
                      "hover:bg-slate-50 dark:hover:bg-slate-800",
                      !notif.read && "bg-indigo-50/50 dark:bg-indigo-950/30",
                    )}
                  >
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      {notif.message}
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                      {notif.time}
                    </p>
                  </DropdownMenu.Item>
                ))
              ) : (
                <div className="px-3 py-6 text-center text-sm text-slate-500">
                  No notifications
                </div>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {/* User Menu */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              data-testid="user-menu-btn"
              className={cn(
                "flex items-center gap-2 p-1.5 rounded-md",
                "hover:bg-slate-100 dark:hover:bg-slate-800",
                "transition-colors duration-150",
              )}
            >
              {globalConfig.currentUser.avatar ? (
                <img
                  src={globalConfig.currentUser.avatar}
                  alt={globalConfig.currentUser.name}
                  className="w-8 h-8 rounded-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    (
                      e.currentTarget.nextElementSibling as HTMLElement | null
                    )?.style.removeProperty("display");
                  }}
                />
              ) : null}
              <span
                className="w-8 h-8 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shrink-0"
                style={
                  globalConfig.currentUser.avatar ? { display: "none" } : {}
                }
              >
                {globalConfig.currentUser.name
                  ? globalConfig.currentUser.name
                      .split(" ")
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()
                  : "?"}
              </span>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {globalConfig.currentUser.name}
                </p>
                <span
                  data-testid="user-role-badge"
                  className={cn(
                    "text-[10px] font-bold uppercase px-1.5 py-0.5 rounded",
                    globalConfig.currentUser.role === "ADMIN"
                      ? "bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-400"
                      : globalConfig.currentUser.role === "OPS"
                        ? "bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400"
                        : "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400",
                  )}
                >
                  {globalConfig.currentUser.role}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400 hidden md:block" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              data-testid="user-menu-dropdown"
              className={cn(
                "min-w-[160px] py-1 rounded-md z-50",
                "bg-white dark:bg-slate-900",
                "border border-slate-200 dark:border-slate-800",
                "shadow-lg animate-fade-in",
              )}
              align="end"
              sideOffset={4}
            >
              <DropdownMenu.Item
                data-testid="user-menu-profile"
                className={cn(
                  "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                  "hover:bg-slate-50 dark:hover:bg-slate-800",
                  "text-slate-700 dark:text-slate-300 outline-none",
                )}
              >
                <User className="w-4 h-4" />
                Profile
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="h-px bg-slate-200 dark:bg-slate-800 my-1" />
              <DropdownMenu.Item
                data-testid="user-menu-signout"
                onClick={() => {
                  logout().then(() => navigate("/login", { replace: true }));
                }}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                  "hover:bg-slate-50 dark:hover:bg-slate-800",
                  "text-rose-600 dark:text-rose-400 outline-none",
                )}
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
};
